import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { features as featureFlags } from '@/lib/api';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  LinkIcon,
  ShieldCheckIcon,
  BoltIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const featuresList = [
  {
    name: 'Unique Referral Links',
    description: 'Generate trackable links for each affiliate. Know exactly who\'s driving your growth with pixel-perfect attribution.',
    icon: LinkIcon,
  },
  {
    name: 'Flexible Commissions',
    description: 'Flat fees, percentages, tiered rewards—build the compensation structure that motivates your community.',
    icon: CurrencyDollarIcon,
  },
  {
    name: 'Real-time Dashboard',
    description: 'Watch referrals roll in live. Your affiliates get their own portal to track earnings and performance.',
    icon: ChartBarIcon,
  },
  {
    name: 'Easy Integrations',
    description: 'Connect with Stripe and be up and running in under 10 minutes.',
    icon: BoltIcon,
  },
  {
    name: 'Custom Rewards',
    description: 'Cash, store credit, free products, exclusive access—reward your advocates however you want.',
    icon: UserGroupIcon,
  },
  {
    name: 'Automated Payouts',
    description: 'Set it and forget it. Automatic payments via Stripe or direct deposit on your schedule.',
    icon: ShieldCheckIcon,
  },
];

const stats = [
  { value: '5 min', label: 'Setup time' },
  { value: '0%', label: 'Hidden fees' },
  { value: '24/7', label: 'Real-time tracking' },
  { value: '∞', label: 'Growth potential' },
];

const steps = [
  {
    number: '1',
    title: 'Create your program',
    description: 'Set your commission structure, customize your portal, and define your terms in minutes.',
  },
  {
    number: '2',
    title: 'Invite advocates',
    description: 'Share your signup link or import existing customers. Everyone gets their own unique referral link.',
  },
  {
    number: '3',
    title: 'Watch it grow',
    description: 'Track referrals in real-time, automate rewards, and scale your word-of-mouth engine.',
  },
];

const marqueeItems = [
  'Affiliate Programs',
  'Referral Tracking',
  'Automated Payouts',
  'Custom Rewards',
  'Real-time Analytics',
  'Embeddable Widgets',
];

export default function Landing() {
  const { isAuthenticated, user } = useAuthStore();
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', company: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const getDashboardLink = () => {
    if (!isAuthenticated) return '/login';
    if (user?.role === 'superadmin') return '/admin';
    if (user?.role === 'org_admin') return '/org';
    return '/dashboard';
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Use the custom domain API URL to comply with CSP
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.venndela.com';
      const response = await fetch(`${apiUrl}/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(demoForm),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setDemoForm({ name: '', email: '', company: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 px-6 lg:px-16 py-6 flex justify-between items-center z-50 mix-blend-difference">
        <Link to="/" className="font-display text-2xl font-bold text-warm-white tracking-tight">
          venndela
        </Link>
        <ul className="hidden md:flex items-center gap-12">
          <li>
            <a href="#features" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Features
            </a>
          </li>
          <li>
            <a href="#how" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              How It Works
            </a>
          </li>
          <li>
            <Link to="/pricing" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
              Pricing
            </Link>
          </li>
          {isAuthenticated ? (
            <li>
              <Link to={getDashboardLink()} className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Dashboard
              </Link>
            </li>
          ) : (
            <li>
              <Link to="/login" className="text-warm-white text-sm font-medium hover:opacity-60 transition-opacity">
                Log In
              </Link>
            </li>
          )}
        </ul>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
        {/* Left - Dark side */}
        <div className="bg-charcoal px-6 lg:px-16 pt-32 pb-16 lg:py-0 flex flex-col justify-center relative overflow-hidden">
          {/* Animated glow */}
          <div className="absolute -top-1/2 -right-1/2 w-full h-[200%] bg-gradient-rust opacity-15 animate-pulse-glow" />

          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-warm-white leading-[1.05] relative z-10 animate-fade-up">
            Turn your customers into your best{' '}
            <em className="text-rust italic">salespeople</em>
          </h1>

          <p className="text-tan text-lg mt-8 max-w-md leading-relaxed relative z-10 animate-fade-up-delay">
            Venndela makes referral programs effortless. Launch in minutes, track everything, and watch your community grow your business for you.
          </p>

          <div className="flex flex-wrap gap-4 mt-10 relative z-10 animate-fade-up-delay-2">
            <Link
              to="/coming-soon"
              className="btn-primary px-8 py-4 text-base relative overflow-hidden shimmer"
            >
              Start Free Trial
            </Link>
            <button
              onClick={() => setShowDemoModal(true)}
              className="btn-secondary px-8 py-4 text-base border-tan text-warm-white hover:bg-warm-white hover:text-charcoal"
            >
              Book a Demo
            </button>
          </div>
        </div>

        {/* Right - Light side with floating cards */}
        <div className="bg-cream flex items-center justify-center relative overflow-hidden min-h-[60vh] lg:min-h-0">
          <div className="relative w-[80%] h-[70%]">
            {/* Floating Card 1 */}
            <div className="absolute top-[10%] left-[5%] bg-warm-white rounded-2xl p-6 shadow-xl animate-float">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-sage flex items-center justify-center text-white font-semibold text-sm">
                  SK
                </div>
                <div>
                  <p className="font-semibold text-sm">Sarah K.</p>
                  <p className="text-xs text-charcoal/60">12 referrals this month</p>
                </div>
              </div>
              <div className="flex justify-between pt-3 border-t border-tan/20">
                <span className="text-xs text-charcoal/60">Conversion Rate</span>
                <span className="font-semibold text-sage">34%</span>
              </div>
            </div>

            {/* Floating Card 2 */}
            <div className="absolute top-[40%] right-0 bg-warm-white rounded-2xl p-6 shadow-xl animate-float-delay">
              <p className="font-serif text-3xl text-rust text-center">$2,847</p>
              <p className="text-xs text-charcoal/60 text-center mt-1">Earned this quarter</p>
            </div>

            {/* Floating Card 3 */}
            <div className="absolute bottom-[15%] left-[15%] bg-warm-white rounded-2xl p-6 shadow-xl animate-float-delay-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-rust flex items-center justify-center text-white font-semibold text-sm">
                  MJ
                </div>
                <div>
                  <p className="font-semibold text-sm">Marcus J.</p>
                  <p className="text-xs text-charcoal/60">Top performer</p>
                </div>
              </div>
              <div className="flex justify-between pt-3 border-t border-tan/20">
                <span className="text-xs text-charcoal/60">Total Earnings</span>
                <span className="font-semibold text-sage">$8,420</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <section className="bg-charcoal py-6 overflow-hidden">
        <div className="flex animate-scroll">
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <span
              key={index}
              className="font-display text-sm font-semibold text-tan whitespace-nowrap px-12 flex items-center gap-4"
            >
              <span className="text-rust">✦</span>
              {item}
            </span>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 lg:py-32 px-6 lg:px-16 bg-warm-white">
        <div className="text-center mb-16">
          <p className="section-label">Features</p>
          <h2 className="section-title">
            Everything you need,<br />nothing you don't
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {featuresList.map((feature) => (
            <div key={feature.name} className="feature-card">
              <div className="w-14 h-14 bg-charcoal flex items-center justify-center mb-6">
                <feature.icon className="w-7 h-7 text-warm-white" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">{feature.name}</h3>
              <p className="text-charcoal/70 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-sage py-20 px-6 lg:px-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center text-warm-white">
              <p className="font-serif text-5xl lg:text-6xl">{stat.value}</p>
              <p className="text-sm mt-2 opacity-80">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="py-24 lg:py-32 px-6 lg:px-16 bg-cream">
        <div className="text-center mb-16">
          <p className="section-label">How It Works</p>
          <h2 className="section-title">Three steps to growth</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 max-w-5xl mx-auto relative">
          {/* Connecting line (hidden on mobile) */}
          <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 bg-gradient-to-r from-rust via-sage to-charcoal" />

          {steps.map((step) => (
            <div key={step.number} className="text-center relative">
              <div className="w-20 h-20 bg-warm-white border-[3px] border-charcoal flex items-center justify-center mx-auto mb-8 relative z-10 transition-all duration-300 hover:bg-rust hover:border-rust hover:text-white hover:scale-110 group">
                <span className="font-serif text-3xl group-hover:text-warm-white">{step.number}</span>
              </div>
              <h3 className="font-display text-xl font-bold mb-3">{step.title}</h3>
              <p className="text-charcoal/70 leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-charcoal py-24 lg:py-32 px-6 lg:px-16 text-center relative overflow-hidden">
        <div className="absolute bottom-[-50%] left-1/2 -translate-x-1/2 w-[150%] h-full bg-gradient-rust opacity-10" />

        <h2 className="font-serif text-4xl md:text-5xl text-warm-white mb-6 relative z-10">
          Ready to grow through community?
        </h2>
        <p className="text-tan text-lg mb-10 relative z-10">
          Start your free 14-day trial. No credit card required.
        </p>
        <Link
          to="/coming-soon"
          className="btn-primary px-10 py-5 text-lg relative z-10 inline-flex items-center gap-2 group"
        >
          Get Started Free
          <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal border-t border-white/10 py-12 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <Link to="/" className="font-display text-xl font-bold text-warm-white">
              venndela
            </Link>

            <ul className="flex flex-wrap justify-center gap-8">
              <li>
                <a href="#features" className="text-tan text-sm hover:text-warm-white transition-colors">
                  Features
                </a>
              </li>
              <li>
                <Link to="/pricing" className="text-tan text-sm hover:text-warm-white transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <a href="#" className="text-tan text-sm hover:text-warm-white transition-colors">
                  Docs
                </a>
              </li>
              <li>
                <a href="#" className="text-tan text-sm hover:text-warm-white transition-colors">
                  Blog
                </a>
              </li>
              <li>
                <a href="mailto:support@venndela.com" className="text-tan text-sm hover:text-warm-white transition-colors">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
            <ul className="flex flex-wrap justify-center gap-6">
              <li>
                <Link to="/legal/terms" className="text-charcoal/50 text-xs hover:text-tan transition-colors">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/legal/privacy" className="text-charcoal/50 text-xs hover:text-tan transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/legal/acceptable-use" className="text-charcoal/50 text-xs hover:text-tan transition-colors">
                  Acceptable Use
                </Link>
              </li>
            </ul>
            <p className="text-charcoal/50 text-sm">
              © {new Date().getFullYear()} Venndela. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Book a Demo Modal */}
      {showDemoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/70 backdrop-blur-sm">
          <div className="rounded-xl bg-warm-white shadow-2xl w-full max-w-md animate-fade-in border border-cream">
            <div className="px-6 py-5 border-b border-cream flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rust/10 rounded-lg flex items-center justify-center">
                  <CalendarDaysIcon className="w-5 h-5 text-rust" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-charcoal">Book a Demo</h3>
                  <p className="text-xs text-charcoal/60">Join our weekly group demo</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowDemoModal(false);
                  setSubmitStatus('idle');
                }}
                className="text-charcoal/40 hover:text-charcoal transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {submitStatus === 'success' ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="font-display text-xl font-semibold text-charcoal mb-2">You're In!</h4>
                <p className="text-charcoal/70 mb-6">
                  We'll send you an invite to our next weekly demo. Check your inbox for details.
                </p>
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    setSubmitStatus('idle');
                  }}
                  className="btn btn-primary"
                >
                  Got it
                </button>
              </div>
            ) : (
              <form onSubmit={handleDemoSubmit} className="p-6 space-y-4">
                <p className="text-sm text-charcoal/70 mb-2">
                  Join other business owners in our weekly live demo. We'll walk through the platform and answer your questions.
                </p>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1.5">Name *</label>
                  <input
                    type="text"
                    value={demoForm.name}
                    onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                    className="input"
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1.5">Email *</label>
                  <input
                    type="email"
                    value={demoForm.email}
                    onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                    className="input"
                    placeholder="you@company.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1.5">Company</label>
                  <input
                    type="text"
                    value={demoForm.company}
                    onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                    className="input"
                    placeholder="Your company (optional)"
                  />
                </div>

                {submitStatus === 'error' && (
                  <p className="text-sm text-red-600">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary w-full mt-2"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Demo Invite'}
                </button>

                <p className="text-xs text-charcoal/50 text-center mt-4">
                  Demos are held weekly. Your information stays private.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
